import './RescheduledRequest.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import TeacherNavbar from '../../../components/TeacherNavbar/TeacherNavbar';
import { Box } from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import { axiosInstance } from '../../../utils/tokenUtils';
import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

const RescheduledRequestDetail = ({
  request: propRequest,
  onBack,
  onAccept,
  onReject,
  actionLoading,
  currentUserId: propCurrentUserId,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId } = useParams();
  const [localActionLoading, setLocalActionLoading] = useState(null);
  const [, forceUpdate] = useState({});
  const [localCurrentUserId, setLocalCurrentUserId] = useState(null);

  const isStandalone = !propRequest; // rendered via route
  const request = propRequest || location.state?.request || null;

  // Use prop currentUserId if available, otherwise use local state
  const currentUserId = propCurrentUserId || localCurrentUserId;

  // Fetch current user if in standalone mode and no currentUserId provided
  useEffect(() => {
    if (isStandalone && !propCurrentUserId && !localCurrentUserId) {
      const fetchCurrentUser = async () => {
        try {
          const response = await axiosInstance.get('/auth/users/me');
          if (response.data && response.data.id) {
            setLocalCurrentUserId(response.data.id);
            console.log('Standalone mode: Current user fetched:', response.data.id);
          }
        } catch (err) {
          console.error('Error fetching current user in standalone mode:', err);
        }
      };
      fetchCurrentUser();
    }
  }, [isStandalone, propCurrentUserId, localCurrentUserId]);

  // Check if current user can approve/reject this request
  const canApproveReject = request && currentUserId && request.requestedTo === currentUserId;

  // Debug logging
  console.log('RescheduledRequestDetail Debug:', {
    requestId: request?.id,
    currentUserId,
    requestedBy: request?.requestedBy,
    requestedTo: request?.requestedTo,
    canApproveReject,
    status: request?.status,
  });

  // Show loading state if currentUserId is not yet loaded
  if (!currentUserId) {
    return (
      <div className="rdx-wrapper">
        <div className="rdx-header">
          <h1 className="rdx-title">Reschedule Requests</h1>
        </div>
        <div className="rdx-content">
          <div className="rdx-message">Loading user information...</div>
        </div>
      </div>
    );
  }

  // Handle approve action
  const handleApprove = async requestId => {
    try {
      setLocalActionLoading(requestId);
      const response = await axiosInstance.patch(
        `/teachers/reschedule-requests/${requestId}/action`,
        {
          status: 'approved',
        }
      );

      if (response.status === 200) {
        // Update the request status locally
        if (request) {
          request.status = 'approved';
          forceUpdate({}); // Force re-render
        }
        // Call the parent onAccept if provided
        if (onAccept) {
          onAccept(requestId);
        }
        // Show success message
        toast.success('Reschedule request approved successfully!');
        console.log('Reschedule request approved successfully');
      }
    } catch (error) {
      console.error('Error approving reschedule request:', error);
      // Handle error with toast
      let errorMessage = 'Failed to approve reschedule request';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setLocalActionLoading(null);
    }
  };

  // Handle reject action
  const handleReject = async requestId => {
    try {
      setLocalActionLoading(requestId);
      const response = await axiosInstance.patch(
        `/teachers/reschedule-requests/${requestId}/action`,
        {
          status: 'rejected',
        }
      );

      if (response.status === 200) {
        // Update the request status locally
        if (request) {
          request.status = 'rejected';
          forceUpdate({}); // Force re-render
        }
        // Call the parent onReject if provided
        if (onReject) {
          onReject(requestId);
        }
        // Show success message
        toast.success('Reschedule request rejected successfully!');
        console.log('Reschedule request rejected successfully');
      }
    } catch (error) {
      console.error('Error rejecting reschedule request:', error);
      // Handle error with toast
      let errorMessage = 'Failed to reject reschedule request';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setLocalActionLoading(null);
    }
  };

  // Determine which loading state to use
  const isLoading = actionLoading === request?.id || localActionLoading === request?.id;

  const getStatusClass = status => {
    switch (status) {
      case 'pending':
        return 'rdx-status--pending';
      case 'approved':
        return 'rdx-status--approved';
      case 'rejected':
        return 'rdx-status--rejected';
      default:
        return 'rdx-status--pending';
    }
  };

  const getStatusIcon = status => {
    switch (status) {
      case 'pending':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" />
          </svg>
        );
      case 'approved':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 12L11 14L15 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
          </svg>
        );
      case 'rejected':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" />
            <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" />
          </svg>
        );
      default:
        return null;
    }
  };

  const formatStatus = status => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const inner = (
    <div className="rdx-wrapper">
      <div className="rdx-header">
        <h1 className="rdx-title">Reschedule Requests</h1>
        {request && request.status === 'pending' && canApproveReject && (
          <div className="rdx-actions rdx-actions--header">
            <button
              className="rdx-btn rdx-btn--reject"
              onClick={() => handleReject(request.id)}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                'Reject'
              )}
            </button>
            <button
              className="rdx-btn rdx-btn--accept"
              onClick={() => handleApprove(request.id)}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                'Accept'
              )}
            </button>
          </div>
        )}
        {request && request.status === 'pending' && !canApproveReject && (
          <div className="rdx-actions rdx-actions--header">
            <div className="rdx-info-message">
              {request.requestedBy === currentUserId
                ? 'You requested this reschedule. Waiting for student approval.'
                : 'This request requires approval from another party.'}
            </div>
          </div>
        )}
      </div>

      <div className="rdx-content">
        {!request ? (
          <div className="rdx-message">No request found{requestId ? ` (#${requestId})` : ''}.</div>
        ) : (
          <>
            <div className="rdx-message">
              <span className="rdx-name">{request.studentName}</span>
              {' has requested to reschedule this session'}
            </div>

            <div className="rdx-section">
              <h3 className="rdx-section-title">Reschedule Date</h3>
              <div className="rdx-datetime-grid">
                <div className="rdx-card">
                  <div className="rdx-card-header">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="rdx-icon-primary"
                    >
                      <rect
                        x="3"
                        y="4"
                        width="18"
                        height="18"
                        rx="2"
                        ry="2"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                      <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                      <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    <span className="rdx-label">Date</span>
                  </div>
                  <div className="rdx-value">{request.rescheduleDate}</div>
                </div>
                <div className="rdx-card">
                  <div className="rdx-card-header">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="rdx-icon-primary"
                    >
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    <span className="rdx-label">Timing</span>
                  </div>
                  <div className="rdx-value">{request.rescheduleTime}</div>
                </div>
              </div>
            </div>

            <div className="rdx-section">
              <h3 className="rdx-section-title">Session Date</h3>
              <div className="rdx-datetime-grid">
                <div className="rdx-card">
                  <div className="rdx-card-header">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="rdx-icon-primary"
                    >
                      <rect
                        x="3"
                        y="4"
                        width="18"
                        height="18"
                        rx="2"
                        ry="2"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                      <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                      <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    <span className="rdx-label">Date</span>
                  </div>
                  <div className="rdx-value">{request.originalDate}</div>
                </div>
                <div className="rdx-card">
                  <div className="rdx-card-header">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="rdx-icon-primary"
                    >
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    <span className="rdx-label">Timing</span>
                  </div>
                  <div className="rdx-value">{request.originalTime}</div>
                </div>
              </div>
            </div>

            <div className="rdx-student-row">
              <img
                src={request.studentAvatar || '/placeholder.svg'}
                alt={request.studentName}
                className="rdx-avatar"
              />
              <div className="rdx-student-info">
                <div className="rdx-student-name">{request.studentName}</div>
                <div className="rdx-student-country">
                  {request.country} <ReactCountryFlag countryCode={request.countryCode} svg />
                </div>
              </div>
            </div>

            <div className="rdx-section">
              <h3 className="rdx-section-title">Reschedule Status</h3>
              <div className={`rdx-status ${getStatusClass(request.status)}`}>
                <div className="rdx-status-content">
                  {getStatusIcon(request.status)}
                  <div className="rdx-status-text">
                    <div className="rdx-status-label">Request</div>
                    <div className="rdx-status-value">{formatStatus(request.status)}</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* {request && request.status === 'pending' && canApproveReject && (
        <div className="rdx-actions rdx-actions--footer">
          <button
            className="rdx-btn rdx-btn--reject"
            onClick={() => handleReject(request.id)}
          >
            Reject
          </button>
          <button
            className="rdx-btn rdx-btn--accept"
            onClick={() => handleApprove(request.id)}
          >
            Accept
          </button>
        </div>
      )} */}
    </div>
  );

  if (!isStandalone) return inner;

  return (
    <Layout title="Klass Online | Reschedule Request Details">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <TeacherNavbar />
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <Box className="reschedule-request-container">{inner}</Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default RescheduledRequestDetail;
